import React from 'react';
import SEO from '@/components/SEO';

const ServicesPage = () => {
  return (
    <div data-testid="services-page">
      <SEO 
        title="Our Services"
        description="Expert project management training, M&E support, and organizational capacity building services in Suriname. We strengthen institutions, improve project performance, and drive sustainable impact."
        keywords="project management training Suriname, monitoring evaluation services, M&E support, organizational capacity building, NGO consulting Suriname, baseline studies"
        url="https://ndebusiness.com/services"
      />
      <section 
        className="section-spacing text-white relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/services-header.jpg)',
          minHeight: '305px'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="content-container relative z-10">
          <h1 className="font-rubik text-4xl md:text-5xl font-bold text-center mb-4" data-testid="services-page-title">
            OUR SERVICES
          </h1>
          <p className="font-roboto text-lg text-center max-w-3xl mx-auto">
            At NDE Training & Consultancy, we provide specialized services that strengthen
            institutions, improve project performance, and drive sustainable impact.
          </p>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          <div className="mb-12" data-testid="service-project-management">
            <h2 className="font-rubik text-3xl font-bold mb-6 text-[#aaae10] text-center">
              PROJECT MANAGEMENT TRAINING
            </h2>
            <div className="font-roboto text-base leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                <p>
                  We deliver comprehensive training programs covering the full project cycle,
                  from concept to completion. Our approach combines theory with practical tools
                  to ensure immediate application.
                </p>
                <h3 className="font-rubik text-lg font-semibold mt-4 mb-3">Key areas include:</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Project design and proposal development</li>
                  <li>Fundraising strategies and donor engagement</li>
                  <li>Project planning, implementation, and risk management</li>
                  <li>Monitoring, evaluation, and learning (MEL)</li>
                  <li>Donor reporting and compliance</li>
                </ul>
              </div>
              <div className="space-y-4">
                <p>
                  Our trainings are designed to build strong, confident teams capable of
                  delivering high-quality projects.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-12 bg-[#f5f5f5] p-8 rounded-lg" data-testid="service-me">
            <h2 className="font-rubik text-3xl font-bold mb-4 text-[#aaae10] text-center">
              MONITORING & EVALUATION (M&E) SUPPORT
            </h2>
            <p className="font-rubik text-xl font-semibold text-center mb-8 text-[#646464]">
              Turn Data into Decisions. Measure What Matters.
            </p>
            
            <div className="font-roboto text-base leading-relaxed space-y-6">
              <p>
                At NDE Training & Consultancy, we help organizations move beyond reporting for donors—we support you in building practical Monitoring & Evaluation systems that improve performance, accountability, and real impact. Monitoring & Evaluation (M&E) is not just about collecting data—it's about understanding what works, what doesn't, and how to improve your projects and programs effectively.
              </p>

              <h3 className="font-rubik text-2xl font-bold mt-8 mb-4 text-[#aaae10]">What We Offer</h3>
              <p>
                We provide hands-on, tailored M&E support for NGOs, community-based organizations, and development projects:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start mt-6">
                <div className="space-y-4">
                  <ul className="list-disc pl-6 space-y-3">
                    <li>
                      <strong>M&E System Design</strong><br />
                      We develop simple, effective frameworks aligned with your project goals and donor requirements.
                    </li>
                    <li>
                      <strong>Indicator Development</strong><br />
                      We help you define clear, measurable indicators that truly reflect your impact.
                    </li>
                    <li>
                      <strong>Data Collection Tools</strong><br />
                      From basic templates to structured tools, we ensure your team can easily collect reliable data.
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <ul className="list-disc pl-6 space-y-3">
                    <li>
                      <strong>Capacity Building & Training</strong><br />
                      We train your staff to confidently implement and manage M&E systems.
                    </li>
                    <li>
                      <strong>Data Analysis & Reporting</strong><br />
                      We turn your data into meaningful insights and professional reports.
                    </li>
                    <li>
                      <strong>Project Reviews & Evaluations</strong><br />
                      Mid-term and final evaluations to assess performance and guide future planning.
                    </li>
                  </ul>
                </div>
              </div>

              <h3 className="font-rubik text-2xl font-bold mt-8 mb-4 text-[#aaae10]">Our Approach</h3>
              <p>We believe M&E should be:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start mt-4">
                <div>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Simple</strong> – Easy to understand and use</li>
                    <li><strong>Practical</strong> – Designed for real field conditions</li>
                  </ul>
                </div>
                <div>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Relevant</strong> – Focused on decision-making, not just reporting</li>
                    <li><strong>Empowering</strong> – Building your team's internal capacity</li>
                  </ul>
                </div>
              </div>
              <p className="mt-4">
                We work closely with your team to ensure systems are owned, used, and sustained beyond the project lifecycle.
              </p>

              <h3 className="font-rubik text-2xl font-bold mt-8 mb-4 text-[#aaae10]">Who This Service Is For</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <ul className="list-disc pl-6 space-y-2">
                  <li>NGOs and Foundations</li>
                  <li>Community-Based Organizations (CBOs)</li>
                </ul>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Government and Development Projects</li>
                  <li>Corporate Social Responsibility (CSR) Programs</li>
                </ul>
              </div>

              <h3 className="font-rubik text-2xl font-bold mt-8 mb-4 text-[#aaae10]">The Result</h3>
              <p>With our M&E support, your organization will:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mt-4">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Track progress with confidence</li>
                  <li>Meet donor requirements with ease</li>
                </ul>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Improve project performance</li>
                  <li>Demonstrate real, measurable impact</li>
                </ul>
              </div>

              <div className="mt-8 p-6 bg-white rounded-lg border-l-4 border-[#aaae10]">
                <p className="font-rubik text-xl font-semibold text-[#646464]">
                  Let's Strengthen Your Impact
                </p>
                <p className="mt-2">
                  Whether you are starting a new project or improving an existing one, we are ready to support you with practical, results-oriented M&E solutions.
                </p>
              </div>
            </div>
          </div>

          <div data-testid="service-capacity-building">
            <h2 className="font-rubik text-3xl font-bold mb-6 text-[#aaae10] text-center">
              ORGANIZATIONAL CAPACITY BUILDING
            </h2>
            <div className="font-roboto text-base leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                <p>
                  We support organizations in strengthening their structures, systems, and
                  performance. Our consultancy services are flexible and tailored to specific
                  organizational needs. We also conduct in-depth baseline studies that provide the foundation for successful project design and implementation. Our work ensures that
                  interventions are data-driven, context-specific, and measurable.
                </p>
                <h3 className="font-rubik text-lg font-semibold mt-4 mb-3">
                  Areas of support includes:
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Socio-economic and environmental assessments</li>
                  <li>Economic valuation studies for protected areas</li>
                  <li>Stakeholder mapping and analysis</li>
                </ul>
              </div>
              <div className="space-y-4">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Strategic planning and program development</li>
                  <li>Awareness and capacity-building sessions</li>
                  <li>External evaluations and donor-funded project assessments</li>
                </ul>
                <p className="mt-4">
                  Our goal is to help organizations become more effective, accountable, and
                  sustainable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#aaae10] text-white">
        <div className="content-container text-center">
          <h2 className="font-rubik text-3xl font-bold mb-4">READY TO GET STARTED?</h2>
          <p className="font-roboto text-lg mb-6 max-w-2xl mx-auto">
            Contact us today to discuss how our services can support your organization's goals
            and drive sustainable impact.
          </p>
          <a href="/contact" className="btn-secondary" data-testid="services-cta-button">
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
